// pages/ProductsPage.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, type NavigateOptions } from 'react-router-dom';
import { listProducts, addFavoriteProduct, removeFavoriteProduct } from '../api/endpoints';
import type { Product, ProductListResponse } from '../types/api';
import useScanCredits from '../hooks/useScanCredits';
import { API_BASE_URL } from '../api/config';
import { ProductSearchModal } from '../components/ProductSearchModal';
import ConfirmationDialog from '../components/ui/ConfirmationDialog';
import { getErrorMessage } from '../utils/errors';

// ————— Icons —————
const ic = 'w-[18px] h-[18px]';
const IconBox = () => (
    <svg viewBox="0 0 24 24" className={ic} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
        <path d="m3.3 7 8.7 5 8.7-5" />
        <path d="M12 22V12" />
    </svg>
);
const IconSearch = () => (
    <svg viewBox="0 0 24 24" className={ic} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.3-4.3" />
    </svg>
);
const IconSend = () => (
    <svg viewBox="0 0 24 24" className={ic} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="m4 4 16 8-16 8 5-8-5-8Z" />
    </svg>
);
const IconCompare = () => (
    <svg viewBox="0 0 24 24" className={ic} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 17h6" />
        <path d="M15 17h6" />
        <path d="M6 17V7" />
        <path d="M18 17V7" />
        <path d="M6 7 3 10" />
        <path d="M18 7 21 10" />
    </svg>
);
const IconDoc = () => (
    <svg viewBox="0 0 24 24" className={ic} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2h7l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Z" />
        <path d="M13 2v5h5" />
    </svg>
);
const IconCheck = () => (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="m20 6-11 11-5-5" />
    </svg>
);

// ————— Utils —————
const normalizeImage = (url?: string | null) => {
    if (!url) return null;
    if (/^https?:/i.test(url)) return url;
    const base = API_BASE_URL.endsWith('/') ? API_BASE_URL : `${API_BASE_URL}/`;
    try { return new URL(url, base).toString(); } catch { return null; }
};

// ————— Types —————
type Selection = Record<number, Product>;

type PrefillState = {
    prefill: {
        barcodeA?: string;
        barcodeB?: string;
        productIdA?: number;
        productIdB?: number;
    };
};

type FloatingSelectionBarProps = {
    count: number;
    canPairSelected: boolean;
    outOfQuota: boolean;
    onClear: () => void;
    onSendCompat: () => void;
    onSendCompare: () => void;
    onBlocked: () => void;
};

type ProductCardProps = {
    item: Product;
    selectionMode: boolean;
    selected: boolean;
    onTap: () => void;
    onLongPress: () => void;
    onAnalyze: () => void;
    onFavorite: () => void;
    onDetails: () => void;
};

function FloatingSelectionBar({
    count,
    canPairSelected,
    outOfQuota,
    onClear,
    onSendCompat,
    onSendCompare,
    onBlocked,
}: FloatingSelectionBarProps): React.JSX.Element {
    const clickCompat = () => {
        if (outOfQuota) onBlocked();
        else onSendCompat();
    };
    const clickCompare = () => {
        if (outOfQuota) onBlocked();
        else onSendCompare();
    };

    return (
        <div className="selection-bar" style={{ bottom: 'calc(64px + env(safe-area-inset-bottom, 0px) + var(--s-2))' }}>
            <div className="selection-bar__inner">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--s-3)' }}>
                    <span className="t-small" style={{ fontWeight: 500, color: 'var(--c-deep-teal)' }}>{count} selected</span>
                    <button
                        type="button"
                        onClick={onClear}
                        className="t-label"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                    >
                        Clear
                    </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--s-2)', marginTop: 'var(--s-2)' }}>
                    <button
                        type="button"
                        onClick={clickCompat}
                        disabled={!canPairSelected || outOfQuota}
                        className="btn btn--primary btn--sm btn--full"
                    >
                        <IconSend />Compatibility
                    </button>
                    <button
                        type="button"
                        onClick={clickCompare}
                        disabled={!canPairSelected || outOfQuota}
                        className="btn btn--ghost btn--sm btn--full"
                    >
                        <IconCompare />Comparison
                    </button>
                </div>
            </div>
        </div>
    );
}

function ProductCard({
    item,
    selectionMode,
    selected,
    onTap,
    onLongPress,
    onAnalyze,
    onFavorite,
    onDetails,
}: ProductCardProps): React.JSX.Element {
    const image = normalizeImage(item.image_url);
    const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const cancelTimer = () => {
        if (pressTimer.current) clearTimeout(pressTimer.current);
    };

    const onPointerDown = () => {
        cancelTimer();
        pressTimer.current = setTimeout(() => { onLongPress(); }, 350);
    };

    const handleActivate = () => {
        cancelTimer();
        onTap();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleActivate();
        }
    };

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={handleActivate}
            onKeyDown={handleKeyDown}
            onPointerDown={onPointerDown}
            onPointerUp={cancelTimer}
            onPointerLeave={cancelTimer}
            className={`card card--bordered card--lift${selected ? ' card--selected' : ''}`}
            style={{ padding: 'var(--s-4)', position: 'relative' }}
        >
            {(selectionMode || selected) && (
                <div className={`product-select-dot${selected ? ' product-select-dot--active' : ''}`}>
                    {selected && <IconCheck />}
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '5rem 1fr', gap: 'var(--s-4)' }}>
                {image ? (
                    <div className="product-thumb">
                        <img loading="lazy" src={image} alt={item.name} />
                    </div>
                ) : (
                    <div className="product-thumb product-thumb--empty">
                        <span className="t-label" style={{ fontSize: '9px' }}>No image</span>
                    </div>
                )}

                <div className="stack-2" style={{ minWidth: 0, paddingRight: 'var(--s-2)' }}>
                    <div style={{ fontWeight: 600, fontSize: 'var(--fs-small)', color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', letterSpacing: '-0.01em' }}>
                        {item.name}
                    </div>
                    <div className="t-small t-mute" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {item.brand}
                    </div>
                    <div className="row-3" style={{ marginTop: 'var(--s-1)', flexWrap: 'wrap' }}>
                        <span className="pill">{item.barcode ? 'Barcode' : 'No Barcode'}</span>
                        {(item.ingredient_count ?? 0) > 0 && (
                            <span className="pill pill--teal">{item.ingredient_count} Ingredients</span>
                        )}
                    </div>
                </div>

                <div style={{ gridColumn: '1 / -1', marginTop: 'var(--s-4)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--s-2)' }}>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onFavorite(); }}
                            className={`product-action product-action--save${item.is_favorite ? ' is-active' : ''}`}
                        >
                            <div className="product-action__icon">
                                <svg viewBox="0 0 24 24" className={ic} fill={item.is_favorite ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 21s-6.4-4.35-9.2-8.06C1.02 11.66 1 8.65 3.2 6.4 5.4 4.2 8.7 4.1 10.9 6.3L12 7.41l1.1-1.1c2.2-2.2 5.5-2.1 7.7.1 2.2 2.24 2.18 5.26.2 6.54C18.4 16.65 12 21 12 21Z" />
                                </svg>
                            </div>
                            <span className="product-action__label">{item.is_favorite ? 'Saved' : 'Save'}</span>
                        </button>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onAnalyze(); }}
                            className="product-action product-action--analyze"
                        >
                            <div className="product-action__icon"><IconSend /></div>
                            <span className="product-action__label">Analyze</span>
                        </button>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onDetails(); }}
                            className="product-action product-action--detail"
                        >
                            <div className="product-action__icon"><IconDoc /></div>
                            <span className="product-action__label">Details</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ————— Page —————
export default function ProductsPage(): React.JSX.Element {
    const nav = useNavigate();
    const [items, setItems] = useState<Product[]>([]);
    const [meta, setMeta] = useState<{ total: number; offset: number; limit: number; next?: number | null }>({ total: 0, offset: 0, limit: 20 });
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hintTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [selectionMode, setSelectionMode] = useState(false);
    const [selection, setSelection] = useState<Selection>({});
    const [hint, setHint] = useState<string | null>(null);
    const [searchingProduct, setSearchingProduct] = useState<{ keyword: string; title: string } | null>(null);
    const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);

    const { outOfQuota } = useScanCredits('product_barcode');

    const load = useCallback(async (offset: number, append: boolean, q: string) => {
        if (append) setLoadingMore(true);
        else setLoading(true);
        setError(null);
        try {
            const res: ProductListResponse = await listProducts({ limit: meta.limit, offset, q });
            setMeta({ total: res.total, offset: res.offset, limit: res.limit, next: res.next_offset ?? null });
            setItems((prev) => (append ? [...prev, ...res.items] : res.items));
        } catch (error_) {
            setError(getErrorMessage(error_, 'Failed to load products'));
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [meta.limit]);

    useEffect(() => {
        void load(0, false, '');
    }, [load]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setSearchQuery(val);
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => {
            void load(0, false, val);
        }, 1000);
    };

    const selectedItems = useMemo(() => Object.values(selection), [selection]);
    const canPairSelected = useMemo(() => selectedItems.length === 2, [selectedItems]);

    const showHint = useCallback((msg: string) => {
        setHint(msg);
        if (hintTimeout.current) clearTimeout(hintTimeout.current);
        hintTimeout.current = setTimeout(() => { setHint(null); }, 1800);
    }, []);

    const toggleSelection = useCallback((product: Product) => {
        setSelection((prev) => {
            if (product.id in prev) {
                const { [product.id]: _removed, ...rest } = prev;
                return rest;
            }
            if (Object.keys(prev).length >= 2) {
                showHint('You can select up to 2 products.');
                return prev;
            }
            return { ...prev, [product.id]: product };
        });
    }, [showHint]);

    const enterSelectionWith = (p: Product) => {
        setSelectionMode(true);
        setSelection({ [p.id]: p });
    };
    const clearSelection = () => { setSelection({}); setSelectionMode(false); };

    const handleFavorite = async (p: Product) => {
        const isFav = !!p.is_favorite;
        setItems((prev) => prev.map((item) => (item.id === p.id ? { ...item, is_favorite: !isFav } : item)));
        try {
            if (isFav) {
                await removeFavoriteProduct(p.id);
                showHint('Removed from favorites');
            } else {
                await addFavoriteProduct(p.id);
                showHint('Added to favorites');
            }
        } catch (error_) {
            setItems((prev) => prev.map((item) => (item.id === p.id ? { ...item, is_favorite: isFav } : item)));
            showHint(getErrorMessage(error_, 'Failed to update favorites'));
        }
    };

    const makeNavigateAction = useCallback((target: string, payload: NavigateOptions): () => void => {
        return () => { nav(target, payload); };
    }, [nav]);

    const makeAnalysisAction = useCallback((p: Product): () => void => {
        return () => {
            nav(`/scanner/result/${p.id}`, { state: { barcode: p.barcode ?? '', source: 'db' } });
        };
    }, [nav]);

    const openDetails = (p: Product) => {
        nav(`/products/${p.id}`, { state: { barcode: p.barcode, source: 'catalog' } });
    };

    const sendToAnalysis = (p: Product) => {
        setConfirmAction(() => makeAnalysisAction(p));
    };

    const guardAndGo = (target: string, payload: NavigateOptions) => {
        if (outOfQuota) {
            showHint('Out of credits. Top up or wait for your monthly reset.');
            return;
        }
        setConfirmAction(() => makeNavigateAction(target, payload));
    };

    const sendSelection = (mode: 'compatibility' | 'comparison') => {
        if (!canPairSelected) return;
        const [a, b] = selectedItems;
        const target = mode === 'compatibility' ? '/compatibility' : '/comparison';
        guardAndGo(target, {
            state: {
                prefill: {
                    barcodeA: a.barcode ?? '',
                    barcodeB: b.barcode ?? '',
                    productIdA: a.id,
                    productIdB: b.id,
                },
            } satisfies PrefillState,
        });
    };

    const toggleSelectionMode = () => {
        if (selectionMode) clearSelection();
        else setSelectionMode(true);
    };

    const handleLoadMore = () => {
        const next = meta.next;
        if (next != null) void load(next, true, searchQuery);
    };

    return (
        <div
            className={`stack-5${selectedItems.length > 0 ? ' sm:pb-0' : ''}`}
            style={{
                maxWidth: '64rem',
                marginInline: 'auto',
                paddingBottom: selectedItems.length > 0 ? 'calc(var(--s-10) + env(safe-area-inset-bottom, 0px))' : undefined,
            }}
        >
            <div className="card card--bordered card--flat" style={{ padding: 'var(--s-5)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--s-3)' }}>
                    <div className="icon-badge icon-badge--teal"><IconBox /></div>
                    <button
                        type="button"
                        onClick={toggleSelectionMode}
                        className="btn btn--ghost btn--sm"
                    >
                        {selectionMode ? 'Done' : 'Select'}
                    </button>
                </div>

                <div className="stack-2" style={{ marginTop: 'var(--s-3)' }}>
                    <h1 className="t-h3" style={{ margin: 0 }}>Product <em className="t-italic">Database</em></h1>
                    <p className="t-small t-mute" style={{ margin: 0 }}>Browse our entire collection. Search by brand, name, or barcode.</p>
                </div>

                <div className="search-input" style={{ marginTop: 'var(--s-4)' }}>
                    <div className="search-input__icon"><IconSearch /></div>
                    <input
                        type="text"
                        placeholder="Search products…"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className="search-input__field"
                    />
                </div>

                {hint && (
                    <div className="hint-toast" style={{ marginTop: 'var(--s-3)' }}>{hint}</div>
                )}
            </div>

            {loading && !loadingMore && (
                <div className="card card--bordered card--flat" style={{ padding: 'var(--s-9)', textAlign: 'center' }}>
                    <p className="t-small t-mute" style={{ margin: 0 }}>Loading products…</p>
                </div>
            )}

            {error && (
                <div className="card card--error" style={{ padding: 'var(--s-5)' }}>
                    <p className="t-small" style={{ margin: 0 }}>{error}</p>
                </div>
            )}

            {!loading && !error && (
                <div className="stack-4">
                    {items.map((item) => (
                        <ProductCard
                            key={item.id}
                            item={item}
                            selectionMode={selectionMode}
                            selected={!!selection[item.id]}
                            onTap={() => {
                                if (selectionMode) toggleSelection(item);
                                else openDetails(item);
                            }}
                            onLongPress={() => {
                                if (selectionMode) toggleSelection(item);
                                else enterSelectionWith(item);
                            }}
                            onAnalyze={() => { sendToAnalysis(item); }}
                            onFavorite={() => { void handleFavorite(item); }}
                            onDetails={() => { openDetails(item); }}
                        />
                    ))}

                    {items.length === 0 && (
                        <div className="drop-zone drop-zone--static" style={{ padding: 'var(--s-9) var(--s-5)' }}>
                            <div className="drop-zone__icon drop-zone__icon--teal"><IconSearch /></div>
                            <p className="t-small" style={{ fontWeight: 600, color: 'var(--ink)', margin: 'var(--s-2) 0 0' }}>No products found</p>
                            <p className="t-small t-mute" style={{ margin: 'var(--s-1) 0 0' }}>Try a different search term or check back later.</p>
                        </div>
                    )}
                </div>
            )}

            {meta.next != null && (
                <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 'var(--s-6)' }}>
                    <button
                        type="button"
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        className="btn btn--ghost"
                    >
                        {loadingMore ? 'Loading…' : 'Load more products'}
                    </button>
                </div>
            )}

            {selectedItems.length > 0 &&
                createPortal(
                    <FloatingSelectionBar
                        count={selectedItems.length}
                        canPairSelected={canPairSelected}
                        outOfQuota={outOfQuota}
                        onClear={clearSelection}
                        onSendCompat={() => { sendSelection('compatibility'); }}
                        onSendCompare={() => { sendSelection('comparison'); }}
                        onBlocked={() => { showHint('Out of credits. Top up or wait for your monthly reset.'); }}
                    />,
                    document.body,
                )}

            {searchingProduct && (
                <ProductSearchModal
                    keyword={searchingProduct.keyword}
                    titleOverride={searchingProduct.title}
                    onClose={() => { setSearchingProduct(null); }}
                />
            )}

            <ConfirmationDialog
                open={!!confirmAction}
                onClose={() => { setConfirmAction(null); }}
                onConfirm={() => {
                    if (confirmAction) confirmAction();
                    setConfirmAction(null);
                }}
                title="Confirm Scan"
                description="This will use credits. Are you sure you want to proceed?"
                confirmLabel="Proceed"
                cancelLabel="Cancel"
            />
        </div>
    );
}
